import { useCallback, useSyncExternalStore } from 'react';

/**
 * Learner-adjustable text size (accessibility). Scales the root font-size, so
 * every rem-based size in the app (Tailwind's text-* utilities) grows
 * proportionally. Inline style on <html> wins over any stylesheet rule, so the
 * three presets are reliable regardless of the base theme.
 *
 * Persisted in localStorage and hydrated before React mounts (bootstrap in
 * main.tsx) so the first paint is already the chosen size, mirroring useTheme.
 */

export type FontScale = 'normal' | 'large' | 'larger';

const STORAGE_KEY = 'hcc:ui:font-scale';

const ROOT_SIZE: Record<FontScale, string> = {
  normal: '100%',
  large: '112.5%',
  larger: '125%',
};

export function readFontScale(): FontScale {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'normal' || raw === 'large' || raw === 'larger') return raw;
  } catch {
    // localStorage can throw in private browsing; fall through.
  }
  return 'normal';
}

function writeFontScale(scale: FontScale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, scale);
  } catch {
    // Best-effort persistence.
  }
}

export function applyFontScale(scale: FontScale) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.fontSize = ROOT_SIZE[scale];
}

/** Hydrate before React mounts. Called from main.tsx. */
export function bootstrapFontScale(): FontScale {
  const scale = readFontScale();
  applyFontScale(scale);
  return scale;
}

const listeners = new Set<() => void>();
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function getSnapshot(): FontScale {
  return readFontScale();
}
function getServerSnapshot(): FontScale {
  return 'normal';
}

export function useFontScale(): { scale: FontScale; setScale: (next: FontScale) => void } {
  const scale = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const setScale = useCallback((next: FontScale) => {
    writeFontScale(next);
    applyFontScale(next);
    for (const l of listeners) l();
  }, []);
  return { scale, setScale };
}
